/**
 * ChecklistItem Component
 *
 * Individual checklist item with touch-friendly controls.
 * Supports completion, notes, and photo evidence.
 *
 * @module pages/technician/checklists/ChecklistItem
 */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaCheck,
  FaCircle,
  FaCamera,
  FaStickyNote,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaExclamationTriangle,
  FaMinus,
} from 'react-icons/fa';
import { CHECKLIST_ITEM_STATUS } from '../../../utils/constants';
import './ChecklistView.css';

const ChecklistItem = ({
  item,
  onComplete,
  onUncomplete,
  onAddPhoto,
  onAddNote,
  onMarkNA,
  onSkip,
  disabled = false,
}) => {
  const [showNotes, setShowNotes] = useState(false);
  // The column is `notes`. `completion_notes` does not exist on
  // `project_checklist_items`, so the textarea always opened empty and the
  // "Nota" indicator never lit up.
  const [noteText, setNoteText] = useState(item.notes || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const itemRef = useRef(null);

  const isCompleted = item.status === CHECKLIST_ITEM_STATUS.COMPLETED;
  const isNA = item.status === CHECKLIST_ITEM_STATUS.NOT_APPLICABLE;
  const isSkipped = item.status === CHECKLIST_ITEM_STATUS.SKIPPED;
  const isPending = item.status === CHECKLIST_ITEM_STATUS.PENDING;
  const hasPhoto = !!item.photo_url;
  const hasNote = !!item.notes;
  // `project_checklist_items` has a single text column, `description` — there is
  // no `title`. Rendering `item.title` printed nothing, so every row in the list
  // was an anonymous checkbox.
  const label = item.description || 'Item sin descripcion';

  // Handle touch start for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  // Handle touch move for swipe
  const handleTouchMove = (e) => {
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY.current);

    // Only handle horizontal swipes
    if (deltaY < 30 && Math.abs(deltaX) > 10) {
      // Limit swipe distance
      const newSwipeX = Math.max(-120, Math.min(120, deltaX));
      setSwipeX(newSwipeX);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (swipeX > 80 && isPending) {
      // Swipe right - complete
      handleToggleComplete();
    } else if (swipeX < -80 && isPending) {
      // Swipe left - show options
      setIsExpanded(true);
    }
    setSwipeX(0);
  };

  // Toggle completion
  const handleToggleComplete = () => {
    if (disabled) return;

    if (isCompleted) {
      onUncomplete && onUncomplete(item.id);
    } else if (isPending) {
      onComplete && onComplete(item.id, { notes: noteText || undefined });
    }
  };

  // Handle checkbox click
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    handleToggleComplete();
  };

  // Handle add photo
  const handleAddPhoto = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onAddPhoto && onAddPhoto(item.id);
  };

  // Handle save note
  const handleSaveNote = () => {
    if (disabled) return;
    onAddNote && onAddNote(item.id, noteText);
    setShowNotes(false);
  };

  // Handle mark as N/A
  const handleMarkNA = () => {
    if (disabled) return;
    onMarkNA && onMarkNA(item.id);
    setIsExpanded(false);
  };

  // Handle skip
  const handleSkip = () => {
    if (disabled) return;
    onSkip && onSkip(item.id);
    setIsExpanded(false);
  };

  // Get status icon
  //
  // Es la accion principal de la pantalla, asi que los dos estados que SE PUEDEN
  // tocar (completado / pendiente) son <button> de verdad: antes eran <div
  // onClick> sin role, sin tabIndex y sin handler de teclado, o sea invisibles
  // para el teclado y para un lector de pantalla. Los estados que NO son
  // accionables (N/A, omitido) se quedan como texto con nombre accesible: no
  // hay nada que activar ahi.
  const getStatusIcon = () => {
    if (isCompleted) {
      return (
        <button
          type="button"
          className="checklist-item-checkbox completed"
          onClick={handleCheckboxClick}
          disabled={disabled}
          aria-pressed
          aria-label={`Desmarcar «${label}»`}
        >
          <FaCheck />
        </button>
      );
    }
    if (isNA) {
      return (
        <span className="checklist-item-checkbox na" role="img" aria-label="No aplica">
          <FaMinus />
        </span>
      );
    }
    if (isSkipped) {
      return (
        <span className="checklist-item-checkbox skipped" role="img" aria-label="Omitido">
          <FaTimes />
        </span>
      );
    }
    return (
      <button
        type="button"
        className={`checklist-item-checkbox pending ${disabled ? 'disabled' : ''}`}
        onClick={handleCheckboxClick}
        disabled={disabled}
        aria-pressed={false}
        aria-label={`Marcar «${label}» como completado`}
      >
        <FaCircle />
      </button>
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('es-CR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      ref={itemRef}
      className={`checklist-item ${isCompleted ? 'completed' : ''} ${isNA ? 'na' : ''} ${isSkipped ? 'skipped' : ''}`}
      style={{ transform: `translateX(${swipeX}px)` }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe action indicators */}
      {swipeX > 40 && (
        <div className="swipe-action-indicator swipe-complete">
          <FaCheck /> Completar
        </div>
      )}
      {swipeX < -40 && (
        <div className="swipe-action-indicator swipe-options">
          Opciones
        </div>
      )}

      {/* Main item content */}
      <div className="checklist-item-content">
        {getStatusIcon()}

        <div className="checklist-item-info" onClick={() => !disabled && setIsExpanded(!isExpanded)}>
          <div className="checklist-item-title">{label}</div>

          {/* Completion info */}
          {(isCompleted || isNA || isSkipped) && item.completed_at && (
            <div className="checklist-item-completion-info">
              <span className="completion-time">{formatTimestamp(item.completed_at)}</span>
              {item.completed_by_profile && (
                <span className="completion-user">por {item.completed_by_profile.full_name}</span>
              )}
            </div>
          )}

          {/* Indicators */}
          <div className="checklist-item-indicators">
            {/* No "Requiere foto" indicator: `requires_photo` is not a column on
                project_checklist_items, so it could never fire. */}
            {hasPhoto && (
              <span className="indicator has-photo">
                <FaCamera /> Foto adjunta
              </span>
            )}
            {hasNote && (
              <span className="indicator has-note">
                <FaStickyNote /> Nota
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="checklist-item-actions">
          {isPending && (
            <>
              <button
                type="button"
                className="action-btn photo-btn"
                onClick={handleAddPhoto}
                disabled={disabled}
                title="Agregar foto"
              >
                <FaCamera />
              </button>
              <button
                type="button"
                className="action-btn note-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNotes(!showNotes);
                }}
                disabled={disabled}
                title="Agregar nota"
              >
                <FaStickyNote />
              </button>
            </>
          )}
          <button
            type="button"
            className="action-btn expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Ocultar opciones del item' : 'Ver opciones del item'}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      </div>

      {/* Expanded section */}
      {isExpanded && (
        <div className="checklist-item-expanded">
          {/* Note section */}
          {(showNotes || hasNote) && (
            <div className="note-section">
              <label className="note-label">Notas:</label>
              {isPending ? (
                <>
                  <textarea
                    className="note-input"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Agregar notas..."
                    rows={3}
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    className="save-note-btn"
                    onClick={handleSaveNote}
                    disabled={disabled || !noteText.trim()}
                  >
                    Guardar nota
                  </button>
                </>
              ) : (
                <p className="note-text">{item.notes}</p>
              )}
            </div>
          )}

          {/* Photo preview */}
          {hasPhoto && (
            <div className="photo-preview-section">
              <label className="photo-label">Foto de evidencia:</label>
              <img
                src={item.photo_url}
                alt="Evidencia"
                className="photo-preview"
                onClick={() => window.open(item.photo_url, '_blank')}
              />
            </div>
          )}

          {/* Additional actions for pending items */}
          {isPending && (
            <div className="expanded-actions">
              <button
                type="button"
                className="expanded-action-btn na-btn"
                onClick={handleMarkNA}
                disabled={disabled}
              >
                <FaMinus /> Marcar N/A
              </button>
              <button
                type="button"
                className="expanded-action-btn skip-btn"
                onClick={handleSkip}
                disabled={disabled}
              >
                <FaExclamationTriangle /> Omitir
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ChecklistItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    // Derived by checklistService.deriveItemStatus — there is no `status`
    // column on project_checklist_items.
    status: PropTypes.string.isRequired,
    is_completed: PropTypes.bool,
    photo_url: PropTypes.string,
    notes: PropTypes.string,
    completed_at: PropTypes.string,
    completed_by_profile: PropTypes.shape({
      id: PropTypes.string,
      full_name: PropTypes.string,
    }),
  }).isRequired,
  onComplete: PropTypes.func,
  onUncomplete: PropTypes.func,
  onAddPhoto: PropTypes.func,
  onAddNote: PropTypes.func,
  onMarkNA: PropTypes.func,
  onSkip: PropTypes.func,
  disabled: PropTypes.bool,
};

export default ChecklistItem;
